import { useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { RequireAlert } from '../../pages/inventory-management/InventoryManagementCollectDeposit';
import ValidatedInput, { ValidatedState } from '../custom-input/ValidatedInput';
import Tooltip from '../tooltip/Tooltip';
import { AlertSVG } from '../svgs';
import { AccountRoles, AppRoles, Validators } from '../../types/enums';
import { PermissionService } from '../../services/permission.service';
import {
  INVENTORY_MANAGEMENT_CONSTANTS,
  LABEL_CONSTANTS,
  NOTIFICATION_CONSTANTS,
  PAYMENT_CONSTANTS,
  TOOLTIP_MESSAGE_CONSTANTS,
} from '../../constants/common';
import './insuranceForm.scss';

export interface ShippingSummary {
  shippingInsurance: string;
  insuranceAmount: ValidatedState;
  insurance: number;
  termsAndConditions: string;
  handlingProcessingFee: number;
  tankRentalFee: number;
  shippingOutBoundCost: number;
  shippingReturnCost: number;
}

type InsuranceFormProps = {
  validate: boolean;
  existingInsurance?: boolean;
  shippingSummary: ShippingSummary;
  setShippingSummary: (summary: ShippingSummary) => void;
  setValid: (valid: boolean) => void;
  promptLabel: string;
  rentTank?: string;
};

const InsuranceForm = ({
  validate,
  existingInsurance = false,
  shippingSummary,
  setShippingSummary,
  setValid,
  promptLabel,
  rentTank = LABEL_CONSTANTS.NO,
}: InsuranceFormProps) => {
  const TANK_RENTAL_FEE = 82.5;
  const HANDLING_PROCESSING_FEE = 10;
  const MIN_INSURANCE_AMOUNT = 1000;
  const MAX_INSURANCE_AMOUNT = 9999;
  const initialFormState = { value: '', valid: false };

  const accountRoles = useAppSelector(state => state.auth.accountContext.roles) as AccountRoles[];
  const appRoles = useAppSelector(state => state.auth.appContext.roles) as AppRoles[];
  const enabled = useAppSelector(state => state.user.enabled);
  const verified = useAppSelector(state => state.user.verified);

  const [userWantsInsurance, setUserWantsInsurance] = useState<string>(shippingSummary.shippingInsurance || '');
  const [insuranceAmount, setInsuranceAmount] = useState<ValidatedState>(shippingSummary.insuranceAmount ?? initialFormState);
  const [insuranceFee, setInsuranceFee] = useState<number>(shippingSummary.insurance ?? 0);

  const permissionService = new PermissionService(accountRoles, appRoles, enabled, verified);

  useEffect(() => {
    const numericAmount = insuranceAmount?.value ? parseFloat(insuranceAmount.value) : 0;
    setInsuranceFee(numericAmount && numericAmount > 0 ? numericAmount * 0.05 : 0);
  }, [insuranceAmount]);

  useEffect(() => {
    validateForm();
    updateShippingSummary();
  }, [userWantsInsurance, insuranceAmount, insuranceFee, rentTank]);

  const validateForm = () => {
    setValid(
      userWantsInsurance === LABEL_CONSTANTS.NO ||
        (userWantsInsurance === LABEL_CONSTANTS.YES && (insuranceAmount?.valid ?? false) && (insuranceFee ?? 0) > 0),
    );
  };

  const updateShippingSummary = () => {
    setShippingSummary({
      shippingInsurance: userWantsInsurance,
      insuranceAmount: insuranceAmount,
      insurance: insuranceFee,
      termsAndConditions: shippingSummary?.termsAndConditions || '',
      handlingProcessingFee: !existingInsurance && userWantsInsurance === LABEL_CONSTANTS.YES ? HANDLING_PROCESSING_FEE : 0,
      tankRentalFee: !existingInsurance && rentTank === LABEL_CONSTANTS.YES ? TANK_RENTAL_FEE : 0,
      shippingOutBoundCost: existingInsurance ? 0 : shippingSummary?.shippingOutBoundCost || 0,
      shippingReturnCost: existingInsurance ? 0 : shippingSummary?.shippingReturnCost || 0,
    });
  };

  useEffect(() => {
    if (userWantsInsurance === LABEL_CONSTANTS.NO) {
      setInsuranceAmount(initialFormState);
      setInsuranceFee(0);
    }
  }, [userWantsInsurance]);

  return (
    <div className="shipping-insurance">
      <h3>{LABEL_CONSTANTS.SHIPPING_INSURANCE}</h3>
      <div className="form-row">
        <label className="radio-row-label">
          <div>
            *{promptLabel}:
            <RequireAlert isActive={validate && userWantsInsurance == undefined} />
          </div>
        </label>
        <div className="radio-row">
          &ensp;
          <input
            type="radio"
            name="insurance-yes"
            checked={userWantsInsurance === LABEL_CONSTANTS.YES}
            onChange={() => setUserWantsInsurance(LABEL_CONSTANTS.YES)}
          />
          <label>{LABEL_CONSTANTS.YES}</label>
          <input
            type="radio"
            name="insurance-no"
            checked={userWantsInsurance === LABEL_CONSTANTS.NO}
            onChange={() => setUserWantsInsurance(LABEL_CONSTANTS.NO)}
          />
          <label>{LABEL_CONSTANTS.NO}</label>
        </div>
      </div>

      {userWantsInsurance === LABEL_CONSTANTS.NO && !existingInsurance && (
        <div className="validated-input-message-error">
          <AlertSVG /> <p>{NOTIFICATION_CONSTANTS.NO_INSURANCE_MESSAGE}</p>
        </div>
      )}

      {userWantsInsurance === LABEL_CONSTANTS.YES && (
        <div>
          {!existingInsurance && (
            <div className="insurance-amount-prompt">
              <label>{INVENTORY_MANAGEMENT_CONSTANTS.INSURANCE_AMOUNT_PROMPT}</label>
            </div>
          )}
          <div className="form-row">
            <ValidatedInput
              useId="Amount"
              label={`*${existingInsurance ? PAYMENT_CONSTANTS.ADDITIONAL_AMOUNT : PAYMENT_CONSTANTS.AMOUNT} ($):`}
              type="number"
              validate={validate}
              setValidatedState={setInsuranceAmount}
              validators={[Validators.REQUIRED]}
              validatedStateForAutoFill={insuranceAmount}
              allowFraction
              preventNegative
              decimalPlaces={2}
              min={!existingInsurance ? MIN_INSURANCE_AMOUNT : undefined}
              max={permissionService.userHasRSGAdminPermission() ? undefined : MAX_INSURANCE_AMOUNT}
            />
          </div>
          <div className="form-row">
            <label>{`${PAYMENT_CONSTANTS.INSURANCE_FEE}:`}</label>
            <div className="input-container">
              <div className="calc-insurance-amount">
                ${insuranceFee.toFixed(2)}
                <Tooltip>{TOOLTIP_MESSAGE_CONSTANTS.INSURANCE_FEE_TOOLTIP}</Tooltip>
              </div>
            </div>
          </div>
          <br />
        </div>
      )}
      <br />
    </div>
  );
};

export default InsuranceForm;
